import { useState, useRef, useCallback, useEffect } from 'react';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { useAuthUser } from '@/hooks/useAuthUser';
import { toast } from 'sonner';
import { bakeLottieToWebM, fileIsLottieJson, readLottieJsonFromFile } from '@/modules/lottie/bake/bakeLottieToWebM';

export interface VideoUploadResult {
  success: boolean;
  videoUrl?: string;
  error?: string;
}

export interface VideoUploadState {
  isUploading: boolean;
  tempVideo: string;
  tempFile: File | null;
  error: string | null;
}

export interface VideoUploadOptions {
  maxSizeInMB?: number;
  autoUpload?: boolean;
}

export const useVideoUpload = (
  contextId?: string,
  options: VideoUploadOptions = {}
) => {
  const {
    maxSizeInMB = 500,
    autoUpload = false
  } = options;

  const services = useServices();
  const { user } = useAuthUser();
  const userId = user?.id;
  
  const [state, setState] = useState<VideoUploadState>({
    isUploading: false,
    tempVideo: '',
    tempFile: null,
    error: null
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cleanup temp video URLs
  useEffect(() => {
    return () => {
      if (state.tempVideo && state.tempVideo.startsWith('blob:')) {
        try { 
          URL.revokeObjectURL(state.tempVideo); 
        } catch {
          // Revoking is best-effort cleanup
        }
      }
    };
  }, [state.tempVideo]);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }));
  }, []);

  const setUploading = useCallback((isUploading: boolean) => {
    setState(prev => ({ ...prev, isUploading }));
  }, []);

  const setTempVideo = useCallback((tempVideo: string) => {
    setState(prev => ({ ...prev, tempVideo }));
  }, []);

  const setTempFile = useCallback((tempFile: File | null) => {
    setState(prev => ({ ...prev, tempFile }));
  }, []);

  const validateFile = useCallback((file: File): { isValid: boolean; error?: string } => {
    // Check file size
    if (file.size > maxSizeInMB * 1024 * 1024) {
      return {
        isValid: false,
        error: `File size must be less than ${maxSizeInMB}MB`
      };
    }

    // Check file type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    const allowedExtensions = ['.mp4', '.webm', '.mov', '.avi'];
    const hasValidType = allowedTypes.includes(file.type) ||
                        allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!hasValidType) {
      return {
        isValid: false,
        error: 'File must be a video (MP4, WebM, MOV, AVI)'
      };
    }

    return { isValid: true };
  }, [maxSizeInMB]);

  const uploadFile = useCallback(async (
    file: File
  ): Promise<VideoUploadResult> => {
    if (!userId) {
      return {
        success: false,
        error: 'User not authenticated'
      };
    }

    try {
      setUploading(true);
      setError(null);

      const timestamp = Date.now();

      // Lottie inputs are baked to transparent WebM here so downstream code never sees JSON.
      let fileToUpload = file;
      let uploadExtension = file.name.split('.').pop() || 'mp4';
      if (await fileIsLottieJson(file)) {
        const bakeToastId = toast.loading('Baking Lottie to video…');
        try {
          const lottieJson = await readLottieJsonFromFile(file);
          const bake = await bakeLottieToWebM(lottieJson, {
            onProgress: (current, total) => {
              toast.loading(`Baking Lottie to video… ${Math.round((current / total) * 100)}%`, { id: bakeToastId });
            },
          });
          const bakedName = file.name.replace(/\.(json|lottie)$/i, '') + '.webm';
          fileToUpload = new File([bake.blob], bakedName, { type: bake.blob.type || 'video/webm' });
          uploadExtension = 'webm';
          toast.success(`Lottie baked (${bake.frameCount} frames)`, { id: bakeToastId });
        } catch (bakeErr) {
          toast.error(bakeErr instanceof Error ? bakeErr.message : 'Lottie bake failed', { id: bakeToastId });
          throw bakeErr;
        }
      }

      const videoFilename = `scene-video-${timestamp}_${Math.random().toString(36).substring(2)}.${uploadExtension}`;
      const videoFilePath = `ui-videos/${contextId || userId}/${videoFilename}`;

      const { data: stored, error: uploadError } = await services.storage.uploadFile(
        videoFilePath,
        fileToUpload,
        {
          contentType: fileToUpload.type,
          cacheControl: '3600',
          upsert: false
        }
      );

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      // The address the upload answered: the stored name has a version in it, so nothing is cached under it before
      if (!stored?.url) {
        throw new Error('Failed to get public URL for uploaded video');
      }
      const videoUrl = stored.url;

      setUploading(false);
      return {
        success: true,
        videoUrl
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);
      setUploading(false);
      return {
        success: false,
        error: errorMessage
      };
    }
  }, [userId, contextId, services.storage, setUploading, setError]);

  const handleFileSelect = useCallback(async (file: File) => {
    const validation = validateFile(file);
    if (!validation.isValid) {
      setError(validation.error || 'Invalid file');
      toast.error(validation.error || 'Invalid file');
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setTempVideo(previewUrl);
    setTempFile(file);

    if (autoUpload) {
      const result = await uploadFile(file);
      if (!result.success) {
        toast.error(result.error || 'Upload failed');
      }
    }
  }, [validateFile, autoUpload, uploadFile, setError, setTempVideo, setTempFile]);

  const reset = useCallback(() => {
    if (state.tempVideo && state.tempVideo.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(state.tempVideo);
      } catch {
        // Revoking is best-effort cleanup
      }
    }
    setState({
      isUploading: false,
      tempVideo: '',
      tempFile: null,
      error: null
    });
  }, [state.tempVideo]);

  return {
    ...state,
    fileInputRef,
    uploadFile,
    handleFileSelect,
    reset,
    setError,
    setUploading,
    setTempVideo,
    setTempFile
  };
};

